import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type CompetencyEvidenceStatus = 'pending' | 'approved' | 'rejected';

export interface CompetencyEvidenceUpload {
  id: number;
  activity_id: number;
  user_id: number;
  uploaded_by: number;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  status: CompetencyEvidenceStatus;
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  approved_at: string | null;
  expires_at: string | null;
  company_id: number | null;
  created_at: string;
}

// One evidence-type activity the colaborador must satisfy, with their latest upload.
export interface MyEvidenceActivity {
  id: number;
  instruction: string | null;
  validity_months: number | null;
  approver_type: string | null;
  is_required: boolean;
  position_competency: { id: number; name: string; position: { id: number; name: string } };
  evidence_uploads: CompetencyEvidenceUpload[];
}

// Pending evidence a reviewer may act on (adds owner + competency context).
export interface PendingEvidence extends CompetencyEvidenceUpload {
  owner: { id: number; first_name: string; last_name: string };
  activity: {
    id: number;
    approver_type: string | null;
    approval_group_id: number | null;
    validity_months: number | null;
    position_competency: { id: number; name: string };
  } | null;
}

export interface UploadEvidenceInput {
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  user_id?: number;
}

export interface ReviewEvidenceInput {
  status: 'approved' | 'rejected';
  review_notes?: string;
}

const KEYS = { all: ['competency-evidence'] as const };

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: KEYS.all });
  // An approval flips the competency status matrix, so refresh it too.
  qc.invalidateQueries({ queryKey: ['competency'] });
}

export function useMyCompetencyEvidence() {
  return useQuery({
    queryKey: [...KEYS.all, 'mine'],
    queryFn: () => api.get<MyEvidenceActivity[]>('/me/competency-evidence'),
    select: (res) => res.data,
  });
}

export function useUploadCompetencyEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ activityId, data }: { activityId: number; data: UploadEvidenceInput }) =>
      api.post(`/competency-activities/${activityId}/evidence`, data),
    onSuccess: () => invalidate(qc),
  });
}

export function usePendingCompetencyEvidence(enabled = true) {
  return useQuery({
    queryKey: [...KEYS.all, 'pending'],
    queryFn: () => api.get<PendingEvidence[]>('/competency-evidence/pending'),
    select: (res) => res.data,
    enabled,
  });
}

export function useReviewCompetencyEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ReviewEvidenceInput }) =>
      api.post(`/competency-evidence/${id}/review`, data),
    onSuccess: () => invalidate(qc),
  });
}
